import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { FinanceSetupStep } from "@/components/onboarding/finance-setup-step"
import { WelcomeStep } from "@/components/onboarding/welcome-step"
import { listPendingInvites } from "@/http/invites"
import { logger } from "@/lib/logger"
import { useBreadcrumbs } from "@/store/breadcrumbs"
import { useOrganization } from "@/store/organization"

type TOnboardingStep = "welcome" | "finance-setup"

export const Route = createFileRoute("/_authenticated/onboarding")({
  beforeLoad: async () => {
    const { organizations, loadOrganizations } = useOrganization.getState()

    // Load organizations if not already loaded
    if (organizations.length === 0) {
      await loadOrganizations()
    }

    const { organizations: loadedOrgs } = useOrganization.getState()

    // If user already has organizations, redirect to home
    if (loadedOrgs.length > 0) {
      logger.info("User has organizations, redirecting from onboarding to home")
      throw redirect({ to: "/home" })
    }

    const pendingInvites = await listPendingInvites()
    if (pendingInvites.data?.invites?.length) {
      logger.info("Pending invites found - redirecting to invites")
      throw redirect({ to: "/invites" })
    }

    const { setCurrentPath } = useBreadcrumbs.getState()
    setCurrentPath({ title: "Onboarding", path: "/onboarding" })

    return null
  },
  loader: () => {
    return {
      title: "Onboarding",
      path: "/onboarding",
    }
  },
  component: OnboardingPage,
})

function OnboardingPage() {
  const [step, setStep] = useState<TOnboardingStep>("welcome")
  const navigate = useNavigate()
  const { setCurrentOrg, loadOrganizations } = useOrganization()

  useEffect(() => {
    // Prevent back navigation during onboarding
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault()
      window.history.pushState(null, "", window.location.href)
    }

    window.history.pushState(null, "", window.location.href)
    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  const handleWelcomeComplete = async (orgId: string) => {
    logger.info("Welcome step completed", { organizationId: orgId })

    // Reload organizations list to get the newly created organization
    await loadOrganizations()

    // Find the newly created organization in the list
    const { organizations } = useOrganization.getState()
    const newOrg = organizations.find((org) => org.id === orgId)

    if (!newOrg) {
      logger.error("Failed to find organization after creation", { orgId })
      // Move to finance setup anyway - it will show error if org is not set
      setStep("finance-setup")
      return
    }

    // Set as current organization
    setCurrentOrg(newOrg)

    logger.info("Organization set as current", { slug: newOrg.slug })

    // Move to finance setup step
    setStep("finance-setup")
  }

  const handleFinanceSetupComplete = async () => {
    logger.info("Finance setup completed")
    // Redirect to dashboard
    const { currentOrg } = useOrganization.getState()
    if (currentOrg) {
      navigate({
        to: "/$orgSlug/dashboard",
        params: { orgSlug: currentOrg.slug },
      })
    } else {
      navigate({ to: "/home" })
    }
  }

  const handleFinanceSetupSkip = async () => {
    logger.info("Finance setup skipped")
    // Redirect to dashboard
    const { currentOrg } = useOrganization.getState()
    if (currentOrg) {
      navigate({
        to: "/$orgSlug/dashboard",
        params: { orgSlug: currentOrg.slug },
      })
    } else {
      navigate({ to: "/home" })
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center p-4">
      {step === "welcome" && <WelcomeStep onComplete={handleWelcomeComplete} />}
      {step === "finance-setup" && (
        <FinanceSetupStep
          onComplete={handleFinanceSetupComplete}
          onSkip={handleFinanceSetupSkip}
        />
      )}
    </div>
  )
}
